/**
 * The user's watchlists, as the add-players drawer's Filters menu needs them
 * (SE-6554).
 *
 * ## Why not `useWatchlistQuery`
 *
 * That hook answers a different question: which SINGLE watchlist is selected
 * app-wide. To do it, it writes `watchListSelectedVar` from an `onCompleted`
 * callback and prepends a synthetic "All Watchlists" entry. Neither belongs to
 * a multi-select filter — the synthetic row would render as a checkbox that
 * means the same thing as checking nothing, and a second call site writing the
 * shared reactive var would let opening the drawer change what the scouting
 * page shows. This hook reads the same query with no side effects and returns
 * only real watchlists.
 *
 * Both hooks issue the SAME query with the same (empty) variables, so Apollo's
 * request deduplication collapses them onto one network read and one normalized
 * cache entry — the drawer's menu and the tab's list cannot disagree about
 * which watchlists exist.
 * @module features/player-kanban/hooks/useWatchlistFilterOptions
 */

import { useMemo } from "react";

import { useKanbanListMyUserWatchlistsQuery } from "@/generated/graphql";

import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";

/** A watchlist as the filter menu consumes it. */
export interface WatchlistFilterOption {
  /** Watchlist id — what the pool query filters on. */
  readonly id: string;
  /** Display name for the menu row. */
  readonly name: string;
}

/**
 * Read the user's watchlists for the drawer's filter menu.
 * @param skip - Skip the read (the drawer is closed). A board whose drawer is
 * never opened fires no watchlist read at all.
 * @returns The user's watchlists, whether they are loading, and whether the
 * read actually SUCCEEDED (see the `isLoadedSuccessfully` field below).
 */
const useWatchlistFilterOptions = (
  skip: boolean
): {
  readonly watchlists: readonly WatchlistFilterOption[];
  readonly isLoading: boolean;
  /**
   * True only once the read has actually completed WITHOUT error. An empty
   * `watchlists` array is ambiguous on its own — it reads the same whether
   * the query hasn't resolved yet, errored out (a failed
   * `useKanbanListMyUserWatchlistsQuery` also yields `data: undefined`, i.e.
   * an empty `watchlists`), or genuinely found zero watchlists. A caller
   * that needs to tell "confirmed zero" apart from "unknown" — e.g. the
   * add-players drawer's watchlist pool count (SE-7096) — must gate on
   * this, not on `!isLoading` alone, or a network error gets reported as a
   * trustworthy zero.
   */
  readonly isLoadedSuccessfully: boolean;
} => {
  // SE-7071: explicit `first` for the same reason as `useWatchlistQuery` —
  // and the SAME value, so the two surfaces cannot disagree about which
  // watchlists exist (this hook's stated dedup intent).
  const { data, loading, error } = useKanbanListMyUserWatchlistsQuery({
    skip,
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
  });

  const watchlists = useMemo<readonly WatchlistFilterOption[]>(
    () =>
      data?.listMyUserWatchlists?.edges?.map(edge => ({
        id: edge.node.watchlist.id,
        name: edge.node.watchlist.name,
      })) ?? [],
    [data]
  );

  return {
    watchlists,
    isLoading: loading,
    isLoadedSuccessfully: !skip && !loading && !error,
  };
};

export default useWatchlistFilterOptions;
